import { rem } from 'polished'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { DesktopLayoutSizes } from '../ItemCard/Common/utils'

import TextButton from 'LuxKit/Button/TextButton'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import {
  useAttachDocument,
  useDetachDocument,
} from 'tripPlanner/hooks/api/documents'
import type { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'

const Root = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
`

const MainSection = styled.div`
  flex: 1;
`

const StyledLinePaperclipIcon = styled(LinePaperclipIcon)`
  align-self: flex-start;
`

const Updated = styled.span`
  display: none;
  ${DesktopLayoutSizes} {
    display: unset;
  }
`

const StyledStructureLabel = styled(StructureLabel)`
  height: 100%;
`

interface Props {
  document: TripDocumentWithItemInfo
  tripId: string
  tripItemId?: string
}

function MyDocumentsTile({ document, tripId, tripItemId }: Props) {
  const intl = useIntl()
  const isAttached = !!document.item

  const { mutate: attachDocument, isLoading: attaching } = useAttachDocument({
    onSuccess: () => {
      showSnackbar(intl.formatMessage({
        id: '9xXfNcUtEdkWhbY6lx96',
        defaultMessage: 'Document attached successfully',
        description: 'Success notification after attaching a document to a trip item',
      }), 'success')
    },
    onError: () => {
      showSnackbar(intl.formatMessage({
        id: 'vke2UnBtO7BSvTTWdrX_',
        defaultMessage: 'Failed to attach document',
        description: 'Error notification when attaching a document to a trip item fails',
      }), 'critical')
    },
    tripId,
  })
  const { mutate: detachDocument, isLoading: detaching } = useDetachDocument({
    onSuccess: () => {
      showSnackbar(intl.formatMessage({
        id: 'jukJoHiYw-gT4rH6Diqy',
        defaultMessage: 'Document detached successfully',
        description: 'Success notification after detaching a document from a trip item',
      }), 'success')
    },
    onError: () => {
      showSnackbar(intl.formatMessage({
        id: 'R65YrK7jO129t-AzJJ9b',
        defaultMessage: 'Failed to detach document',
        description: 'Error notification when detaching a document from a trip item fails',
      }), 'critical')
    },
    tripId,
  })

  const handleAttach = useCallback(() => {
    if (tripItemId) {
      attachDocument({ tripId, tripItemId, filename: document.filename })
    }
  }, [attachDocument, document.filename, tripId, tripItemId])

  const handleDetach = useCallback(() => {
    if (tripItemId) {
      detachDocument({ tripId, tripItemId, filename: document.filename })
    }
  }, [detachDocument, document.filename, tripId, tripItemId])

  const isMobile = useScreenSizeOnly('mobile')

  const detachLabel = intl.formatMessage({
    id: 'ggXCAf43p1aGooKgyepT',
    defaultMessage: 'Detach',
    description: 'Button label to detach a document from a trip item',
  })
  const attachLabel = intl.formatMessage({
    id: '4EMH7pbRQvkcuOu6ZU6l',
    defaultMessage: 'Attach',
    description: 'Action button label to attach a document to a trip item',
  })

  return (
    <Root>
      <Group direction="horizontal" gap={16} verticalAlign="end">
        <StyledLinePaperclipIcon size="M" />
        <MainSection>
          <Group direction="vertical" gap={12}>
            <div>
              <BodyText variant="large" wrap="letter-wrap">
                {document.filename}
              </BodyText>
              <Caption variant="medium" colour="light-foreground-alt">
                <Updated>
                  {intl.formatMessage({
                    id: 'OP4ufn9X84J_2xwaYzMm',
                    defaultMessage: 'Updated',
                    description: 'Label prefix for document last updated date in the trip planner documents section',
                  })}
                  &nbsp;
                </Updated>
                {intlDateFormatter(document.lastModified, INTL_DMY_CASUAL_FORMAT, intl.locale)}
              </Caption>
            </div>
            {isAttached && (
              <div>
                <StyledStructureLabel variant="standard">
                  <BodyText
                    variant="small"
                    wrap="pre-wrap"
                    lineClamp={isMobile ? 2 : 1}
                  >
                    <FormattedMessage
                      id="CVNfZVZlelPmOriMlz67"
                      defaultMessage="Attached to <link>{itemName}</link>"
                      description="Label showing which trip item a document is attached to"
                      values={{
                        itemName: document.item!.name,
                        link: (chunks) => <TextLink>{chunks}</TextLink>,
                      }}
                    />
                  </BodyText>
                </StyledStructureLabel>
              </div>
            )}
            {isMobile && (
              <>
                {isAttached && (
                  <div>
                    <TextButton onClick={handleDetach} kind="secondary">
                      {detachLabel}
                    </TextButton>
                  </div>
                )}
                {!isAttached && (
                  <div>
                    <TextButton onClick={handleAttach} kind="secondary">
                      {attachLabel}
                    </TextButton>
                  </div>
                )}
              </>
            )}
          </Group>
        </MainSection>
        {!isMobile && (
          <>
            {isAttached && (
              <TextButton
                onClick={handleDetach}
                disabled={detaching}
                kind="secondary"
              >
                {detachLabel}
              </TextButton>
            )}
            {!isAttached && (
              <TextButton
                onClick={handleAttach}
                disabled={attaching}
                kind="secondary"
              >
                {attachLabel}
              </TextButton>
            )}
          </>
        )}
      </Group>
    </Root>
  )
}

export default MyDocumentsTile
